import type {
  Draft,
  Me,
  ScheduledPost,
  ScrapeBatch,
  SurfacedTweet,
  Topic,
  UserApp,
} from '@shared/schemas'

export class ApiError extends Error {
  status: number
  detail?: unknown
  constructor(status: number, message: string, detail?: unknown) {
    super(message)
    this.status = status
    this.detail = detail
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    ...init,
  })
  if (!res.ok) {
    let body: unknown = null
    try { body = await res.json() } catch { /* ignore */ }
    throw new ApiError(res.status, extractErrorMessage(body, res.statusText), body)
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

function extractErrorMessage(body: unknown, fallback: string): string {
  if (!body || typeof body !== 'object') return fallback
  const b = body as Record<string, unknown>
  // Our validator wrapper: { error: 'validation_failed', detail: 'field: msg' }
  if (typeof b.detail === 'string' && b.detail.trim()) return b.detail
  if (typeof b.error === 'string') return b.error
  // Raw zod shape: { success: false, error: { issues: [...] } }
  if (b.error && typeof b.error === 'object') {
    const inner = b.error as Record<string, unknown>
    if (Array.isArray(inner.issues) && inner.issues.length > 0) {
      const first = inner.issues[0] as { path?: unknown[]; message?: string }
      const path = Array.isArray(first.path) ? first.path.join('.') : ''
      const msg = typeof first.message === 'string' ? first.message : 'invalid input'
      return path ? `${path}: ${msg}` : msg
    }
  }
  return fallback
}

export const api = {
  logout: () => call<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  me: () => call<Me>('/api/me'),
  updateMe: (body: { voiceSample: string | null; displayName?: string | null }) =>
    call<{ ok: true }>('/api/me', { method: 'PATCH', body: JSON.stringify(body) }),
  updateAi: (body: { apiKey?: string | null; modelPreference?: string | null }) =>
    call<{ ok: true }>('/api/me/ai', { method: 'PATCH', body: JSON.stringify(body) }),

  listTopics: () => call<Topic[]>('/api/topics'),
  createTopic: (body: {
    name: string
    description?: string
    minFollowers: number
    searchQuery?: string
    promotedAppId?: string | null
  }) => call<{ id: string }>('/api/topics', { method: 'POST', body: JSON.stringify(body) }),
  updateTopic: (
    id: string,
    body: Partial<{
      name: string
      description: string
      minFollowers: number
      searchQuery: string
      promotedAppId: string | null
    }>,
  ) => call<{ ok: true }>(`/api/topics/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteTopic: (id: string) => call<{ ok: true }>(`/api/topics/${id}`, { method: 'DELETE' }),

  generateThread: (body: { input: string; targetTweetCount?: number }) =>
    call<{ tweets: string[] }>('/api/threads/generate', { method: 'POST', body: JSON.stringify(body) }),

  listScheduled: () => call<ScheduledPost[]>('/api/scheduled'),
  createScheduled: (body: {
    kind: 'tweet' | 'thread'
    tweets: string[]
    fireAt: number
    replyToTweetId?: string
  }) => call<{ id: string }>('/api/scheduled', { method: 'POST', body: JSON.stringify(body) }),
  updateScheduled: (
    id: string,
    body: Partial<{ tweets: string[]; fireAt: number; status: 'scheduled' | 'canceled' }>,
  ) => call<{ ok: true }>(`/api/scheduled/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteScheduled: (id: string) => call<{ ok: true }>(`/api/scheduled/${id}`, { method: 'DELETE' }),

  scrapeTweets: (body: {
    source: { url: string; kind: 'home' | 'profile' | 'search' | 'list' | 'bookmarks' | 'other' }
    tweets: { tweetUrl: string; tweetText: string; authorHandle: string }[]
  }) =>
    call<{ batchId: string; count: number }>('/api/engagement/scrape', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  listEngagement: (opts?: { batchId?: string; includeDiscarded?: boolean }) => {
    const params = new URLSearchParams()
    if (opts?.batchId) params.set('batch', opts.batchId)
    if (opts?.includeDiscarded) params.set('discarded', '1')
    const qs = params.toString()
    return call<SurfacedTweet[]>(`/api/engagement${qs ? `?${qs}` : ''}`)
  },
  listBatches: () => call<ScrapeBatch[]>('/api/engagement/batches'),
  captureEngagement: (body: {
    tweetUrl: string
    tweetText: string
    authorHandle: string
    topicId?: string
  }) =>
    call<{ surfacedTweetId: string; variants: string[] }>('/api/engagement/capture', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  draftForTweet: (surfacedId: string) =>
    call<{ variants: string[] }>(`/api/engagement/tweets/${surfacedId}/draft`, { method: 'POST' }),
  approveDraft: (body: { draftId: string; body: string; sendNow: boolean; fireAt?: number }) =>
    call<{ sent?: boolean; tweetId?: string; scheduled?: boolean; scheduledPostId?: string }>(
      '/api/engagement/approve',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  rejectDraft: (id: string) =>
    call<{ ok: true }>(`/api/engagement/reject/${id}`, { method: 'POST' }),
  markDraftSent: (id: string, body: string) =>
    call<{ ok: true }>(`/api/engagement/drafts/${id}/mark-sent`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),
  deleteSurfaced: (id: string) =>
    call<{ ok: true }>(`/api/engagement/${id}`, { method: 'DELETE' }),

  disconnectX: () => call<{ ok: true }>('/api/x/disconnect', { method: 'POST' }),

  listApps: () => call<UserApp[]>('/api/user-apps'),
  createApp: (body: { name: string; url: string; positioning?: string }) =>
    call<{ id: string }>('/api/user-apps', { method: 'POST', body: JSON.stringify(body) }),
  deleteApp: (id: string) => call<{ ok: true }>(`/api/user-apps/${id}`, { method: 'DELETE' }),
}

export type {
  Draft,
  Me,
  ScheduledPost,
  ScrapeBatch,
  SurfacedTweet,
  Topic,
  UserApp,
}
